import { API_BASE } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";
import type { GameState } from "@/types/poker";
import type { TableSummary } from "../hooks/useTableLobby";

interface JoinResult {
  room: string;
  playerId: string;
  token: string;
}

export const apiJoin = ({
  room,
  name,
  authToken,
  gameMode = "tournament",
  sb,
  bb,
  buyIn,
}: {
  room: string;
  name?: string;
  authToken?: string;
  gameMode?: "tournament" | "cash";
  sb?: number;
  bb?: number;
  buyIn?: number;
}) => {
  const body: Record<string, unknown> = { room, game_mode: gameMode };
  if (name) body.name = name;
  if (sb !== undefined) body.sb = sb;
  if (bb !== undefined) body.bb = bb;
  if (buyIn !== undefined) body.buy_in = buyIn;

  return apiFetch<JoinResult>("/api/join", { method: "POST", token: authToken, body });
};

export const apiRebuy = ({
  token,
  room,
  amount,
  authToken,
}: {
  token: string;
  room: string;
  amount: number;
  authToken?: string;
}) =>
  // Player token goes in Authorization header (required by get_bearer_token).
  // Auth JWT goes in X-Auth-Token header for chip deduction.
  apiFetch("/api/rebuy", {
    method: "POST",
    token,
    secondaryToken: authToken,
    body: { room, amount },
  });

export const apiListTables = () => apiFetch<TableSummary[]>("/api/tables");

export const apiQuickJoin = ({
  authToken,
  gameMode,
  bb,
  buyIn,
}: {
  authToken: string;
  gameMode: "tournament" | "cash";
  bb?: number;
  buyIn?: number;
}) => {
  const body: Record<string, unknown> = { game_mode: gameMode };
  if (bb !== undefined) body.bb = bb;
  if (buyIn !== undefined) body.buy_in = buyIn;

  return apiFetch<JoinResult>("/api/quick-join", { method: "POST", token: authToken, body });
};

export const apiState = ({ token, room }: { token: string; room: string }) =>
  apiFetch<GameState>(`/api/state/${encodeURIComponent(room)}`, { token });

export const apiReady = ({ token, room }: { token: string; room: string }) =>
  apiFetch("/api/ready", { method: "POST", token, body: { room } });

export const apiAct = ({
  token,
  room,
  action,
  amount,
}: {
  token: string;
  room: string;
  action: string;
  amount?: number;
}) => {
  const payload: Record<string, unknown> = { action };
  if (typeof amount === "number" && !Number.isNaN(amount)) {
    payload.amount = amount;
  }

  return apiFetch(`/api/act/${encodeURIComponent(room)}`, { method: "POST", token, body: payload });
};

export const apiLeave = ({ token, room }: { token: string; room: string }) =>
  apiFetch(`/api/${encodeURIComponent(room)}/leave`, { method: "POST", token });

export const wsUrl = (room: string, token: string) => {
  // On native (Capacitor), connect directly to production server.
  if (API_BASE) {
    return `wss://app.smartpoker.app/ws/${encodeURIComponent(room)}/${encodeURIComponent(token)}`;
  }

  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  // Use the same host:port the page was served from.
  // In Docker (nginx on :80) this routes through the /ws/ proxy.
  // In dev (Vite on :8080) WS proxy doesn't work, so fall back to :8000.
  const isDev = window.location.port === "8080";
  const wsHost = isDev
    ? `${window.location.hostname}:8000`
    : window.location.host;

  // The token MUST be part of the path, not a query parameter.
  return `${proto}//${wsHost}/ws/${encodeURIComponent(room)}/${encodeURIComponent(token)}`;
};
